import { CoreButton, CoreLoader } from "../components/komodo";
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { useQuiz } from "../context/QuizContext";
import { loadDataSources } from "../utils/dataLoader";
import { getQuizResults } from "../utils/quizHelper";
import { generatePdfHtml } from "../utils/generatePdfHtml";
import Recommendations from "../components/Recommendations";
import ProductCarousel from "../components/ProductCarousel";
import { addToCartFlow, addAllProductsToCart } from "../utils/cart";
import ReviseAnswers from "../components/ReviseAnswers";
import { getProductsData } from "../data/dataHelper";
import productSearchApi from "../services/productSearchApi";
// import sharedListApi from "../services/sharedListApi";
import PreferencesModal from "../components/PreferencesModal";
import FullScreenLoader from "../components/FullScreenLoader";
import baseUrl from "../api/config";
import { getString, getLocaleKey } from "../utils/localizationHelper";
import "./Results.scss";

const Results = () => {
  // Category labels mapping - using localization with fallback
  const getCategoryLabel = (category) => {
    // Try results.product.type first, then fall back to results.pdf.product.category
    let label = getString(`results.product.type.${category}`);
    if (!label || label.startsWith("[")) {
      label = getString(`results.pdf.product.category.${category}`);
    }
    return label || category;
  };

  // Helper to extract price data from myPriceData (Angular populatePriceData logic)
  // Removed legacy getPriceData. Only Magellan API pricing is used.
  // State for products.csv data
  const [productsData, setProductsData] = useState([]);
  const { answers, resetQuiz, checkCompletion } = useQuiz();
  const navigate = useNavigate();
  const [results, setResults] = useState({ groups: [], type: "standard" });
  const [isLoading, setIsLoading] = useState(true);
  const [shareUrl, setShareUrl] = useState("");
  const [pdfHtml, setPdfHtml] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [minicartError, setMinicartError] = useState(null);
  const [isAddToCartLoading, setIsAddToCartLoading] = useState(false);
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);

  // Add to Cart handler for ProductCarousel - uses centralized cart.js addToCartFlow
  const handleAddToCart = async ({ product }) => {
    setMinicartError(null);
    setIsAddToCartLoading(true);
    try {
      await addToCartFlow(product, "in", "en");
      const miniCart = window.miniCart;
      if (miniCart) {
        miniCart.showCart(true);
        miniCart.updateItemCountInHeader();
      }
    } catch (err) {
      console.error("Error adding to cart:", err);
      setMinicartError("Failed to add to cart.");
    } finally {
      setIsAddToCartLoading(false);
    }
  };

  // Prevent double API calls in development (StrictMode) - improved approach
  const hasLoadedRef = React.useRef(false);
  const [hasInitialized, setHasInitialized] = React.useState(false);
  const lastAnswersRef = React.useRef(null); // Track last processed answers

  // Note: Authentication is now managed globally by AuthContext
  // No need to check auth on page load - it's cached at app level

  useEffect(() => {
    // Use embedded products data instead of fetching CSV, similar to master branch Angular implementation
    const productsData = getProductsData(); // Could be extended to support locale
    setProductsData(productsData);
  }, []);

  useEffect(() => {
    // Improved duplicate prevention: check both ref and state
    if (hasLoadedRef.current || hasInitialized) {
      return;
    }

    // Check if answers have actually changed (avoid processing same answers)
    const answersString = JSON.stringify(answers);
    if (lastAnswersRef.current === answersString) {
      return;
    }

    if (!productsData.length) {
      return;
    }

    // Set both flags to prevent duplicates
    hasLoadedRef.current = true;
    setHasInitialized(true);
    lastAnswersRef.current = answersString;
    setHasInitialized(true);

    const isComplete = checkCompletion();

    if (!isComplete) {
      navigate("/quiz");
      setIsLoading(false);
      return;
    }

    // Helper to fetch product details using productSearchApi (matching master branch pattern)
    const fetchProductDetails = async (catalogNumbers) => {
      let productDetails = {};
      if (catalogNumbers.length > 0) {
        try {
          // Use the productSearchApi service like in Angular master branch
          const products = await productSearchApi.listProducts(catalogNumbers, {
            includePricing: false,
          });

          // Transform the results to match existing structure and request pricing for each product
          // This follows the EXACT pattern from master branch results.route.js
          if (products && Array.isArray(products)) {
            console.log(products);
            // Map products and request pricing (matching master branch pattern)
            for (const apiProduct of products) {
              if (apiProduct && (apiProduct.catalogNumber || apiProduct.sku)) {
                const catalogNumber =
                  apiProduct.catalogNumber || apiProduct.sku;

                // Store product details
                productDetails[catalogNumber] = apiProduct;

                // Do NOT request pricing data per product here. Pricing is fetched in batch after details.
              }
            }
          }
        } catch (err) {
          console.error("Error fetching product details:", err);
          // fail silently but log the error
        }
      }
      console.log("productDetails", productDetails);
      return productDetails;
    };

    const loadResults = async () => {
      try {
        // Load data sources (matching Angular resolver pattern)
        const { selectionData } = await loadDataSources();

        // Get quiz results using the same logic as Angular
        const quizResults = getQuizResults(answers, selectionData);
        if (quizResults && quizResults.length > 0) {
          const resultRow = quizResults[0];

          // Process results into groups (matching Angular logic)
          const groups = ["best-solution", "additional-products"];
          const queries = [];
          const skus = [];

          Object.keys(resultRow).forEach((key) => {
            const parts = key.split(":");

            if (
              resultRow[key] != null &&
              parts.length > 0 &&
              groups.includes(parts[0])
            ) {
              if (parts[1].match(/-antibodies$/i)) {
                queries.push({
                  group: parts[0],
                  category: parts[1],
                  type: "search",
                  searchType:
                    parts[1] === "primary-antibodies" ? "typeahead" : "text",
                  path: resultRow[key],
                });
              } else if (
                parts[1].match(/recombinant-proteins$/i) ||
                parts[1].match(/antibody-pairs$/i)
              ) {
                queries.push({
                  group: parts[0],
                  category: parts[1],
                  type: "button",
                  path: resultRow[key],
                });
              } else {
                queries.push({
                  group: parts[0],
                  category: parts[1],
                  type: "product",
                  sku: resultRow[key],
                });
                skus.push(resultRow[key]);
              }
            }
          });

          // Always call API for product details when SKUs are available
          const productDetails = await fetchProductDetails(skus);
          // Fetch pricing data from pricing API
          const pricingData = await productSearchApi.fetchPricingData(skus);

          // Helper to extract price data from pricing API response
          const getPriceDataFromApi = (pricingData, sku) => {
            const skuStr = sku.toString();
            if (!pricingData || !pricingData[skuStr]) {
              console.warn("No price data for SKU:", skuStr);
              return null;
            }
            const priceDetails =
              pricingData[skuStr].pricingList?.pricingDetails?.[0];
            if (!priceDetails) {
              console.warn("No pricingDetails for SKU:", skuStr);
              return null;
            }
            const priceLink =
              priceDetails.hasContactLink || priceDetails.hasActionLink
                ? priceDetails.htmlMessageDisp
                : "";
            return {
              priceString:
                priceLink.length > 0
                  ? priceLink
                  : priceDetails.priceString || priceDetails.price || "",
              price:
                priceLink.length > 0 ? priceLink : priceDetails.price || "",
              userPriceString:
                priceLink.length > 0
                  ? priceLink
                  : priceDetails.userPriceString || "",
              userPrice:
                priceLink.length > 0 ? priceLink : priceDetails.userPrice || "",
              isLink: priceLink.length > 0,
              pricingCurrencyCode: priceDetails.pricingCurrencyCode || "",
              action: priceDetails.action,
              onlinePriceApplicable: priceDetails.onlinePriceApplicable,
            };
          };

          const processedGroups = groups.map((group) => {
            const items = queries
              .filter((item) => item.group === group)
              .map((query) => {
                if (query.type === "product") {
                  const apiProduct = productDetails[query.sku];
                  const productInfo =
                    productsData &&
                    productsData.find(
                      (p) => p.sku.toString() === query.sku.toString(),
                    );
                  // Attach price_data ONLY from pricing API response
                  const price_data = getPriceDataFromApi(
                    pricingData,
                    query.sku,
                  );

                  query.product = {
                    sku: apiProduct?.catalogNumber || query.sku,
                    name: apiProduct?.productTitle || "Missing product",
                    url: apiProduct?.productUrl || "#",
                    size: apiProduct?.productSize || "1 kit",
                    image:
                      apiProduct?.images && apiProduct.images[0]?.path
                        ? apiProduct.images[0].path
                        : "",
                    ...apiProduct, // Spread apiProduct first
                    // Then override with our custom fields
                    price_data,
                    // Use priceString if available and not empty, otherwise "Not orderable online"
                    price:
                      price_data?.priceString &&
                      price_data.priceString.trim() !== ""
                        ? price_data.priceString
                        : "Not orderable online",
                    label: getCategoryLabel(query.category),
                    notes:
                      (productInfo && productInfo.notes) ||
                      resultRow[`${query.category}:notes`] ||
                      resultRow[`${query.category}:description`] ||
                      "",
                    brand:
                      apiProduct?.brand ||
                      resultRow[`${query.category}:brand`] ||
                      "Thermo Scientific™",
                  };
                }
                return query;
              });

            return {
              key: group,
              items: items,
            };
          });

          setResults({ groups: processedGroups, type: "standard" });
        }

        // Generate share URL (matching Angular pattern)
        const shareState = {};
        ["q1", "q2", "q3", "q4", "q5", "q6", "q7"].forEach((key) => {
          shareState[key] = answers[key] ? answers[key][0] : null;
        });
        const currentUrl = window.location.href.split("#")[0];
        const shareParams = Object.values(shareState).filter(Boolean).join("/");
        setShareUrl(`${currentUrl}#/share/${shareParams}`);
      } catch (error) {
        console.error("Error loading results:", error);
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, productsData]); // Remove navigate and checkCompletion to prevent unnecessary re-renders

  // Generate PDF HTML whenever results change
  useEffect(() => {
    if (results && results.groups && results.groups.length > 0) {
      setPdfHtml(generatePdfHtml(results, getString));
    }
  }, [results]);

  // getString is now imported from localizationHelper

  // Navigation functions (matching Angular controller)
  const editPreferences = () => {
    setIsPreferencesModalOpen(true); // Open modal instead of navigating
  };

  const closePreferencesModal = () => {
    setIsPreferencesModalOpen(false);
  };

  const startOver = () => {
    resetQuiz();
    navigate("/");
  };

  // Cart functions (matching Angular controller) - now uses bulk addAllProductsToCart
  const addAllToCart = async () => {
    setMinicartError(null);

    const bestSolutionGroup = results.groups.find(
      (g) => g.key === "best-solution",
    );

    if (bestSolutionGroup) {
      const products = bestSolutionGroup.items
        .filter((item) => item.product && item.type === "product")
        .map((item) => item.product);

      if (products.length > 0) {
        try {
          // Use bulk add to cart with single API call
          const result = await addAllProductsToCart(products, "in", "en");

          if (result.error) {
            throw new Error(result.error);
          }
        } catch (err) {
          console.error("Error adding all products to cart:", err);
          setMinicartError("Failed to add all products to cart.");
        }
      }
    }
  };

  // Export functions (matching Angular controller)
  // const downloadPdf = () => {
  //   if (pdfHtml) {
  //     // Submit hidden form (matching Angular implementation)
  //     const form = document.getElementById("pdf-download-form");
  //     if (form) {
  //       form.submit();
  //     }
  //   }
  // };

  // getPdf function: generates PDF HTML and submits the hidden form (Angular parity)
  const getPdf = () => {
    if (results && results.groups && results.groups.length > 0) {
      // Regenerate PDF HTML in case results changed
      const newPdfHtml = generatePdfHtml(results, getString);
      setPdfHtml(newPdfHtml);

      // Update textarea value directly before submission
      const form = document.getElementById("pdf-download-form");
      if (form) {
        const textarea = form.querySelector('textarea[name="html"]');
        if (textarea) {
          textarea.value = newPdfHtml;
        }
        form.submit();
      }
    }
  };

  const downloadXlsx = () => {
    // Helper to decode HTML entities for Excel (e.g., &trade; -> ™)
    const decodeHtmlEntities = (text) => {
      if (!text) return "";
      const textarea = document.createElement("textarea");
      textarea.innerHTML = text;
      return textarea.value;
    };

    const xlsxData = [];
    const mergeCells = []; // Track cells to merge

    // Add headers
    xlsxData.push([
      getString("results.quick-order-xlsx.quick-order.column.catalog_no"),
      getString("results.quick-order-xlsx.quick-order.column.quantity"),
      getString("results.quick-order-xlsx.quick-order.column.product"),
      getString("results.quick-order-xlsx.quick-order.column.unit_size_price"),
    ]);

    // Add product data
    let currentRow = 1; // Start after header (0-indexed)
    results.groups.forEach((group) => {
      if (group.key === "additional-products") {
        xlsxData.push([
          getString(
            "results.quick-order-xlsx.quick-order.additional-products-separator",
          ),
          "",
          "",
          "",
        ]);
        // Merge cells A to C for this row (columns 0-2)
        mergeCells.push({
          s: { r: currentRow, c: 0 },
          e: { r: currentRow, c: 2 },
        });
        currentRow++;
      }
      group.items.forEach((item) => {
        if (item.product) {
          let price =
            item.product.price && item.product.price.trim() !== ""
              ? item.product.price
              : "Not orderable online";
          xlsxData.push([
            item.product.sku,
            1,
            decodeHtmlEntities(item.product.name),
            price ? `${item.product.size} @ ${price}` : item.product.size,
          ]);
          currentRow++;
        }
      });
    });

    // Create worksheet and workbook
    const worksheet = XLSX.utils.aoa_to_sheet(xlsxData);

    // Apply cell merges
    if (mergeCells.length > 0) {
      worksheet["!merges"] = mergeCells;
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      getString("results.quick-order-xlsx.quick-order.title"),
    );
    // Generate XLSX file and trigger download
    XLSX.writeFile(workbook, "ELISA_Builder_Results.xlsx");
  };

  // Tracking functions (matching Angular controller)
  // ...existing code...

  // ...existing code...

  if (isLoading) {
    return (
      <FullScreenLoader>
        <CoreLoader
          variant="circular"
          functionality="indeterminate"
          size="small"
          information="false"
          alignment="center"
        />
        {/* Removed loading text as requested */}
      </FullScreenLoader>
    );
  }

  // Show warning if product details response is not an object
  const hasProductDetailsError =
    results.groups &&
    results.groups.some(
      (group) =>
        group.items &&
        group.items.some(
          (item) =>
            item.type === "product" &&
            (!item.product || typeof item.product !== "object"),
        ),
    );
  if (hasProductDetailsError) {
    return (
      <div className="results-page">
        <div className="error">
          <h2>{getString("results.error.no_data")}</h2>
          <p>
            The API response format was unexpected. Please check the backend
            response and try again.
          </p>
        </div>
      </div>
    );
  }

  if (!answers || Object.keys(answers).length === 0) {
    return (
      <div className="results-page">
        <div className="no-results">
          <h2>{getString("results.error.no_quiz")}</h2>
          <p>{getString("results.error.complete_quiz")}</p>
          <button onClick={startOver} className="btn btn-primary">
            Take Quiz
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tf-page tf-results">
      {/* Banner Section - Angular style with responsive Edit Preferences button */}
      <div className="tf-banner">
        <div className="tf-container">
          <div className="tf-banner-content">
            <div className="tf-h-h">{getString("results.title")}</div>
            <p
              dangerouslySetInnerHTML={{
                __html: getString("results.summary.standard"),
              }}
            />
            <img
              src={
                process.env.PUBLIC_URL + "/assets/tf-ELISA-results-banner.png"
              }
              alt="ELISA Kits"
              className="tf-banner-image"
            />
            {/* Responsive Edit Preferences button: visible only for 520px to 768px */}
            <button
              type="button"
              onClick={editPreferences}
              className="tf-btn tf-btn-default tf-preferences-button"
              aria-label="Edit preferences"
            >
              <i
                className="tf-komodo-24-icon-edit-default-mono"
                style={{
                  fontSize: 20,
                  verticalAlign: "middle",
                  paddingRight: 3,
                }}
              />
              {getString("results.preferences_label")}
            </button>
          </div>
        </div>
      </div>

      {/* Download Buttons Section - Angular style */}
      <div className="tf-container tf-mb-30 tf-download-buttons tf-download-buttons-flex">
        <CoreButton
          onClick={addAllToCart}
          variant="primary"
          size="large"
          type="button"
        >
          <span slot="content">
            <i className="tf-komodo-24-icon-cart-add-mono" />{" "}
            {getString("results.add_all_to_cart")}
          </span>
        </CoreButton>
        <a
          href={getString("results.download_protocol.url")}
          target="_blank"
          rel="noopener noreferrer"
          className="tf-btn tf-btn-info tf-download-pdf-button"
        >
          <i className="tf-komodo-24-icon-download-mono" />{" "}
          {getString("results.download_protocol")}
        </a>
        <div className="tf-dropdown-parent-relative">
          <CoreButton
            variant="information"
            size="large"
            type="button"
            onClick={() => setShowDropdown((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={showDropdown}
          >
            <span slot="content">
              <i className="tf-komodo-24-icon-download-mono" />{" "}
              {getString("results.download_button")}
            </span>
          </CoreButton>
          {showDropdown && (
            <div className="tf-dropdown__options-container">
              <ul className="tf-dropdown-list-reset">
                <li
                  onClick={() => {
                    setShowDropdown(false);
                    getPdf();
                  }}
                  className="tf-dropdown-option"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setShowDropdown(false);
                      getPdf();
                    }
                  }}
                >
                  {getString("results.download_pdf_label")}
                </li>
                <li
                  onClick={() => {
                    setShowDropdown(false);
                    downloadXlsx();
                  }}
                  className="tf-dropdown-option"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setShowDropdown(false);
                      downloadXlsx();
                    }
                  }}
                >
                  {getString("results.download_xlsx_label")}
                </li>
              </ul>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={editPreferences}
          className="tf-btn tf-btn-default tf-preferences-button tf-edit-preferences-btn"
          aria-label="Edit preferences"
        >
          <i className="tf-komodo-24-icon-edit-default-mono tf-edit-icon-size" />{" "}
          {getString("results.preferences_label")}
        </button>
      </div>

      {/* Recommended Products Section */}
      {results && results.groups && results.groups.length > 0 && (
        <div className="tf-result-rank-strip">
          <Recommendations
            recommendedProducts={(() => {
              const bestSolution = results.groups.find(
                (g) => g.key === "best-solution",
              );
              const products =
                bestSolution?.items
                  .filter((item) => item.type === "product" && item.product)
                  .map((item) => item.product) || [];

              return products;
            })()}
          />
          {/* AddToList buttons are now inside ProductCarousel */}
        </div>
      )}

      {/* Additional Products Section - Pixel-perfect recreation of Angular master branch */}
      {(() => {
        const additionalGroup =
          results.groups &&
          results.groups.find((g) => g.key === "additional-products");

        if (
          additionalGroup &&
          additionalGroup.items &&
          additionalGroup.items.length > 0
        ) {
          return (
            <div
              id="additional-products"
              className="tf-results-carousel"
              key="additional-products"
            >
              <div className="tf-container">
                <h4>{getString("results.group.additional-products")}</h4>
                <ProductCarousel
                  items={additionalGroup.items} // Pass all items (products + search results)
                  onAddToCart={handleAddToCart}
                  isAdditionalProducts={true}
                />
                {/* AddToList buttons are now inside ProductCarousel */}
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Show fullscreen loader for add-to-cart at the top level, overlaying the entire page */}
      {isAddToCartLoading && (
        <FullScreenLoader>
          <CoreLoader
            variant="circular"
            functionality="indeterminate"
            size="small"
            information="false"
            alignment="center"
          />
        </FullScreenLoader>
      )}
      {minicartError && <div className="minicart-error">{minicartError}</div>}

      {/* Revise Answers Section */}
      <div className="tf-results-footer">
        <div className="tf-container">
          <ReviseAnswers editPreferences={editPreferences} />
        </div>
      </div>

      {/* Hidden Share URL */}
      <div className="tf-hidden">
        <input type="text" value={shareUrl} readOnly />
      </div>

      {/* Hidden PDF Download Form (now using dynamic baseUrl) */}
      <form
        id="pdf-download-form"
        method="post"
        action={`${baseUrl}/order/genome-database/antibody/getPdf`}
        target="download-frame"
        className="tf-hidden"
        acceptCharset="UTF-8"
      >
        <input
          type="hidden"
          name="Filename"
          value="ELISA_Builder_Recommendations.pdf"
        />
        <input type="hidden" name="locale" value={getLocaleKey()} />
        <textarea
          name="html"
          style={{ display: "none" }}
          defaultValue={pdfHtml}
          readOnly
        />
      </form>
      <iframe
        name="download-frame"
        id="download-frame"
        style={{ display: "none" }}
        title="PDF Download Frame"
      ></iframe>

      {/* Preferences Modal */}
      <PreferencesModal
        isOpen={isPreferencesModalOpen}
        onClose={closePreferencesModal}
      />
    </div>
  );
};

export default Results;
